import React, { useState } from 'react'

import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'

import { useQueryPaginator } from 'src/components/Table'
import { ASNUnitListTable } from 'src/components/Table/list/ASNUnit'
import { SkuDetailTable } from 'src/components/Table/detail/Sku'
import { DamagedListTable } from 'src/components/Table/list/Damaged'
import { EventListTable } from 'src/components/Table/list/Event'
import { FoundListTable } from 'src/components/Table/list/Found'
import { FreightOrderUnitListTable } from 'src/components/Table/list/FreightOrderUnit'
import { FreightShipmentUnitListTable } from 'src/components/Table/list/FreightShipmentUnit'
import { LostListTable } from 'src/components/Table/list/Lost'
import { ParcelOrderUnitListTable } from 'src/components/Table/list/ParcelOrderUnit'
import { ParcelShipmentUnitListTable } from 'src/components/Table/list/ParcelShipmentUnit'
import { SkuThresholdListTable } from 'src/components/Table/list/SkuThreshold'
import { StatsTable } from 'src/components/Stats/StatsTable'
import { UnitListTable } from 'src/components/Table/list/Unit'
import { SkuThresholdDialog } from 'src/modules/supply_chain/components/SkuThresholdDialog'

import { Sku } from 'src/api/models'
import { skuit } from 'src/services/skuit'
import { useQuery, UseQueryResult } from 'react-query'

import { DetailPage } from 'src/components/Pages/DetailPage'
import { DetailTab } from 'src/components/Pages/Tab'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

interface PathProps {
  guid: string
}

interface StorageTabProps {
  sku: UseQueryResult<Sku>
}

const StorageTab = (props: StorageTabProps) => {
  const { sku } = props
  const [thresholdOpen, setThresholdOpen] = useState(false)

  const { data: stats } = useQuery(['sku-stats', sku.data?.guid], () =>
    skuit.sc.skus.stats(sku.data?.guid || '')
  )

  const thresholdPaginator = useQueryPaginator('threshold-list', skuit.sc.skus.thresholds.list, {
    params: { page_size: 5, skus: sku.data?.guid },
  })
  const unitsPaginator = useQueryPaginator('unit-list', skuit.sc.units.list, {
    params: { page_size: 5, skus: sku.data?.guid },
  })
  const damagedPaginator = useQueryPaginator('damaged-list', skuit.sc.damaged.list, {
    params: { page_size: 5, skus: sku.data?.guid },
  })
  const lostPaginator = useQueryPaginator('lost-list', skuit.sc.lost.list, {
    params: { page_size: 5, skus: sku.data?.guid },
  })
  const foundPaginator = useQueryPaginator('found-list', skuit.sc.found.list, {
    params: { page_size: 5, skus: sku.data?.guid },
  })

  return (
    <>
      {thresholdOpen && (
        <SkuThresholdDialog
          open={thresholdOpen}
          handleClose={() => setThresholdOpen(false)}
          onSave={() => {
            thresholdPaginator.refresh()
          }}
          sku={sku.data}
        />
      )}
      <DetailTab>
        {stats && (
          <StatsTable
            titles={['Total Ordered', 'Total Received', 'Total Shipped']}
            values={[stats.total_ordered, stats.total_received, stats.total_shipped]}
          />
        )}
        <SkuThresholdListTable
          paginator={thresholdPaginator}
          title="SKU Threshold Alerts"
          fields={['target', 'facility', 'minCount', 'refillCount', 'status']}
          extraActions={[
            {
              title: '+',
              onClick: () => {
                setThresholdOpen(true)
              },
            },
          ]}
        />
        <UnitListTable
          paginator={unitsPaginator}
          title="Storage Locations"
          fields={['path', 'dateReceived', 'numUnits']}
        />
        <DamagedListTable
          paginator={damagedPaginator}
          fields={['guid', 'client', 'product', 'path']}
        />
        <LostListTable
          paginator={lostPaginator}
          fields={['client', 'product', 'batch', 'numUnits', 'dateReceived', 'path']}
        />
        <FoundListTable
          paginator={foundPaginator}
          fields={['client', 'product', 'batch', 'numUnits', 'path']}
        />
      </DetailTab>
    </>
  )
}

const InboundTab = () => {
  const { guid } = useParams<PathProps>()
  const asnPaginator = useQueryPaginator('asn-unit-list', skuit.sc.asn.units.list, {
    params: { page_size: 5, skus: guid },
  })

  return (
    <DetailTab>
      <ASNUnitListTable
        paginator={asnPaginator}
        title="Received"
        fields={['asn', 'receivedOn', 'numUnits']}
      />
    </DetailTab>
  )
}

const FreightTab = () => {
  const { guid } = useParams<PathProps>()
  const freightOrderUnitsPaginator = useQueryPaginator(
    'freight-order-unit-list',
    skuit.sc.freight.orders.units.list,
    {
      params: { page_size: 5, skus: guid },
    }
  )
  const freightShipmentUnitsPaginator = useQueryPaginator(
    'freight-shipment-unit-list',
    skuit.sc.freight.shipments.units.list,
    { params: { page_size: 5, skus: guid } }
  )

  return (
    <DetailTab>
      <FreightOrderUnitListTable
        paginator={freightOrderUnitsPaginator}
        title="Ordered"
        fields={['order', 'orderStatus', 'placedOn', 'container', 'status', 'numUnits']}
      />
      <FreightShipmentUnitListTable
        paginator={freightShipmentUnitsPaginator}
        title="Shipping / Shipped"
        fields={['shipment', 'order', 'container', 'status', 'shippedOn', 'numUnits']}
      />
    </DetailTab>
  )
}

const ParcelTab = () => {
  const { guid } = useParams<PathProps>()
  const parcelOrderUnitsPaginator = useQueryPaginator(
    'parcel-order-unit-list',
    skuit.sc.parcel.orders.units.list,
    {
      params: { page_size: 5, skus: guid },
    }
  )
  const parcelShipmentUnitsPaginator = useQueryPaginator(
    'parcel-shipment-unit-list',
    skuit.sc.parcel.shipments.units.list,
    { params: { page_size: 5, skus: guid } }
  )

  return (
    <DetailTab>
      <ParcelOrderUnitListTable
        paginator={parcelOrderUnitsPaginator}
        title="Ordered"
        fields={['order', 'placedOn', 'status', 'numUnits']}
      />
      <ParcelShipmentUnitListTable
        paginator={parcelShipmentUnitsPaginator}
        title="Shipping / Shipped"
        fields={['shipment', 'order', 'status', 'shippedOn', 'numUnits']}
      />
    </DetailTab>
  )
}

const EventsTab = () => {
  const { guid } = useParams<PathProps>()
  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  return (
    <DetailTab>
      <EventListTable
        paginator={eventsPaginator}
        title="SKU Events"
        fields={['type', 'data', 'by', 'on']}
      />
    </DetailTab>
  )
}

export const SkuPage = () => {
  const { guid } = useParams<PathProps>()

  const sku = useQuery(['sku-details', guid], () => skuit.sc.skus.retrieve(guid))

  const refresh = () => {
    sku.refetch()
  }

  return (
    <>
      <DetailPage
        obj={sku}
        title="SKU"
        refresh={refresh}
        breadcrumbs={[
          { title: 'Dashboard', url: '/' },
          { title: 'SKUs', url: '/inventory/skus' },
        ]}
        detailComponent={<SkuDetailTable obj={sku} refresh={refresh} />}
        tabs={['Storage', 'Inbound', 'Freight', 'Parcel', 'Events']}
      >
        <StorageTab sku={sku} />
        <InboundTab />
        <FreightTab />
        <ParcelTab />
        <EventsTab />
      </DetailPage>
    </>
  )
}
